import React from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorBoundary from '../ErrorBoundary';
import Container from '@mui/material/Container';

import sidekick from '@last-rev/cms-sidekick-util';
import Media, { type MediaProps } from '../Media';

const isPRC = process.env.SITE === 'RESOURCE';

export interface ArticleHeadProps {
  title?: string;
  pubDate?: string;
  summary?: string;
  icon?: MediaProps;
  sidekickLookup?: any;
  titleId?: string;
  contentOwners?: Array<{ id: string; contentOwnerName: string; email: string }>;
}

export const ArticleHead = ({
  title,
  pubDate,
  summary,
  icon,
  sidekickLookup,
  titleId,
  contentOwners
}: ArticleHeadProps) => {
  return (
    <ErrorBoundary>
      <Container disableGutters>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {title ? (
            <Typography
              variant="h2"
              component="h1"
              id={titleId}
              mb={1}
              {...sidekick(sidekickLookup?.title)}
              data-testid="ArticleHead-title">
              {title}
            </Typography>
          ) : null}
          {icon ? (
            <Box
              py={1}
              sx={{
                'display': 'none',
                '@media print': {
                  display: 'flex' // Only shows in print
                },
                'justifyContent': 'center',
                'alignItems': 'center',
                'width': 300,
                'height': 200,
                'overflow': 'hidden',
                'flexShrink': 0
              }}>
              <Media
                {...icon}
                priority
                disableInlineSVG
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Container>

      {pubDate ? (
        <Box displayPrint="none">
          <AccessTimeIcon
            fontSize="small"
            sx={{
              width: 16,
              height: 16,
              margin: '7px 8px 0 0',
              verticalAlign: 'top',
              fill: 'midnight.A80'
            }}
          />
          <Typography
            variant="time"
            component="time"
            dateTime={pubDate}
            {...sidekick(sidekickLookup?.pubDate)}
            data-testid="ArticleHead-pubDate">
            {dayjs(pubDate).format('MMM D, YYYY')}
          </Typography>
        </Box>
      ) : null}

      {isPRC && !!contentOwners?.length && (
        <Box displayPrint="none" display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">Content Owner:</Typography>
          {contentOwners.map((owner) => (
            <Typography key={owner.id} variant="body2">
              {owner.contentOwnerName}
            </Typography>
          ))}
        </Box>
      )}

      {summary ? (
        <Typography
          displayPrint="none"
          variant="body2"
          component="p"
          mt={4}
          {...sidekick(sidekickLookup?.summary)}
          data-testid="ArticleHead-summary">
          {summary}
        </Typography>
      ) : null}
    </ErrorBoundary>
  );
};

export default ArticleHead;
